import { formatDateTime } from '../../utils/format';

/**
 * "Who created / last edited the task, and when" (owner decision 4,
 * 2026-10-07): two quiet lines at the bottom of the task edit dialog and in
 * the task's details. `GET /tickets/:id` returns `created_by_name`,
 * `updated_by_name` and `updated_at`; an older server returns no names, and
 * then the line shows the time alone.
 *
 * A server that knows the editor (it sends `updated_by`) but has none for this
 * task (a change made before the column existed, or by the system: SLA
 * flags, hours synced from a work log) shows no "edited" line: "נערך לאחרונה"
 * without a name says nothing true (fix round 2026-10-07).
 */
export interface TaskAuditSource {
  created_at?: string | null;
  opened_at?: string | null;
  created_by_name?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  updated_by_name?: string | null;
}

export interface TaskAuditLine {
  kind: 'created' | 'updated';
  /** i18n key of the lead words ("נוצר ע״י" / "נוצר"). */
  labelKey: string;
  name: string | null;
  date: string;
}

/** Changes within a minute of creation are the creation itself (defaults, first assignee). */
const SAME_MOMENT_MS = 60_000;

const clean = (s: string | null | undefined): string | null => (typeof s === 'string' && s.trim() ? s.trim() : null);

export function taskAuditLines(task: TaskAuditSource | null | undefined): TaskAuditLine[] {
  if (!task) return [];
  const lines: TaskAuditLine[] = [];
  const createdAt = task.created_at || task.opened_at || null;
  const createdName = clean(task.created_by_name);
  if (createdAt) {
    lines.push({ kind: 'created', labelKey: createdName ? 'taskAudit.createdBy' : 'taskAudit.created', name: createdName, date: formatDateTime(createdAt) });
  }
  const updatedAt = task.updated_at || null;
  const updatedName = clean(task.updated_by_name);
  if (updatedAt) {
    const created = createdAt ? new Date(createdAt).getTime() : NaN;
    const updated = new Date(updatedAt).getTime();
    const edited = Number.isNaN(created) || Number.isNaN(updated) || updated - created > SAME_MOMENT_MS;
    const editorUnknown = 'updated_by' in task && !updatedName;
    if (edited && !editorUnknown) {
      lines.push({ kind: 'updated', labelKey: updatedName ? 'taskAudit.updatedBy' : 'taskAudit.updated', name: updatedName, date: formatDateTime(updatedAt) });
    }
  }
  return lines;
}
